import { InputAdornment, MenuItem, Stack } from '@mui/material';
import React, { FC, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
    AutocompleteComponent,
    ControlledCheckbox,
    ControlledDatePicker,
    ControlledInput,
    ControlledNumberInput,
    ControlledSelect,
    FormGrid,
} from '../../../shared';
import { Gender, SupporteeRelation } from '../../enums';
import { getNationalities } from '../../../shared/constants/nationalities.constant';
import { validateOriginOption } from '../../../shared/utils/origins.util';
import { getLanguages } from '../../../shared/constants/languages.constant';
import { ControlledMaskInput } from '../../../shared/components/controlled-mask-input/controlled-mask-input.component';

interface Props {
    field?: string;
    dialogLayout?: boolean;
}

export const SupporteeInputs: FC<Props> = ({ field, dialogLayout }) => {
    const { t } = useTranslation();
    const prefix = field ? `${field}.` : '';
    const { watch, setValue, clearErrors } = useFormContext();
    const watchAnonymous = watch(`${prefix}anonymous`);
    const watchIsPrimary = watch(`${prefix}isPrimary`);

    useEffect(() => {
        if (watchAnonymous) {
            setValue(`${prefix}firstName`, '');
            setValue(`${prefix}lastName`, '');
            setValue(`${prefix}nationalSecurityNumber`, '');
        }
        clearErrors();
    }, [prefix, setValue, clearErrors, watchAnonymous]);

    return (
        <Stack spacing={2}>
            <ControlledCheckbox label={`${t('anonymousLabel')}`} name={`${prefix}anonymous`} />

            <FormGrid size={dialogLayout ? { xs: 12 } : { xs: 12, sm: 6, lg: 3, xl: 2 }}>
                <ControlledInput
                    name={`${prefix}firstName`}
                    label={t('firstNameLabel')}
                    disabled={watchAnonymous}
                    required={!watchAnonymous}
                />

                <ControlledInput
                    name={`${prefix}lastName`}
                    label={t('lastNameLabel')}
                    disabled={watchAnonymous}
                    required={!watchAnonymous}
                />

                <AutocompleteComponent
                    name={`${prefix}relation`}
                    label={t('relationLabel')}
                    defaultValue={null}
                    options={Object.values(SupporteeRelation)}
                    getOptionLabel={(option) =>
                        Object.values(SupporteeRelation).includes(option) ? t(option.toLowerCase()) : option
                    }
                />

                <ControlledSelect name={`${prefix}gender`} label={t('genderLabel')}>
                    <MenuItem value={''}>{t('unknown')}</MenuItem>
                    {Object.values(Gender).map((gender) => (
                        <MenuItem value={gender} key={gender}>
                            {t(gender.toLowerCase())}
                        </MenuItem>
                    ))}
                </ControlledSelect>
            </FormGrid>

            <FormGrid size={dialogLayout ? { xs: 12 } : { xs: 12, sm: 6, lg: 3, xl: 2 }}>
                <ControlledDatePicker name={`${prefix}dateOfBirth`} label={t('dateOfBirthLabel')} />
                <ControlledMaskInput
                    name={`${prefix}nationalSecurityNumber`}
                    label={t('nationalSecurityNumberLabel')}
                    mask="**.**.**-***.**"
                    placeholder={'XX.XX.XX-XXX.XX'}
                    disabled={watchAnonymous}
                />
                <AutocompleteComponent
                    name={`${prefix}nationality`}
                    label={t('nationalityLabel')}
                    options={getNationalities}
                    defaultValue={null}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => validateOriginOption(option, value)}
                />

                <AutocompleteComponent
                    name={`${prefix}language`}
                    label={t('languageLabel')}
                    defaultValue={null}
                    options={getLanguages}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => validateOriginOption(option, value)}
                />
                {!field && !watchIsPrimary && (
                    <ControlledNumberInput
                        name={`${prefix}dependencyPercentage`}
                        label={t('dependencyPercentage')}
                        slotProps={{
                            input: {
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            },
                        }}
                    />
                )}
            </FormGrid>
        </Stack>
    );
};
